/**
 * Generic empty-state card used by blocks that depend on data which
 * may be absent (no slides authored, no items in a feed, etc.).
 *
 * <p>Ported from ThemeMaker's
 * {@code lms-portal/src/components/blocks/_shared/EmptyState.tsx}.
 *
 * <p>Renders a dashed-border card with a title and optional hint.
 * Compact variant tightens padding for inline use inside a tighter
 * section (e.g. inside a HeroSlider that's itself only padded
 * lightly).
 */
import type { CSSProperties, HTMLAttributes } from 'react';

export interface EmptyStateProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  hint?: string;
  compact?: boolean;
  /** Optional source/diagnostic string shown in monospace below the
   *  hint. Use to surface which source slug a rail tried to fetch. */
  diagnostic?: string;
}

export function EmptyState({ title, hint, compact = false, diagnostic, ...rest }: EmptyStateProps) {
  const style: CSSProperties = {
    border: '1px dashed var(--color-border-subtle, rgba(0,0,0,0.12))',
    borderRadius: 'var(--radius-md, 8px)',
    background: 'var(--color-surface-page, #f7f8fa)',
    padding: compact ? '24px 20px' : 'var(--space-12, 48px) var(--space-6, 24px)',
    textAlign: 'center',
    color: 'var(--color-text-tertiary, #6a6f73)',
  };

  return (
    <div {...rest} style={style}>
      <div
        style={{
          fontSize: compact ? 14 : 'var(--font-size-md, 16px)',
          fontWeight: 500,
          color: 'var(--color-text-secondary, #5f6675)',
          marginBottom: hint ? 4 : 0,
        }}
      >
        {title}
      </div>
      {hint && (
        <div
          style={{
            fontSize: compact ? 12 : 'var(--font-size-sm, 13px)',
            opacity: 0.85,
          }}
        >
          {hint}
        </div>
      )}
      {diagnostic && (
        <div
          style={{
            marginTop: 8,
            fontSize: '11px',
            fontFamily: 'var(--font-family-mono, ui-monospace, monospace)',
            opacity: 0.6,
          }}
        >
          {diagnostic}
        </div>
      )}
    </div>
  );
}
